import { matchesEmployee } from './employeeSearch'

/**
 * The seniority roster (docs/product/reports.md): every employee in scope in
 * seniority order, with their team, title, shift, qualifications, and whether
 * they are active or on detail. Pure, so the screen and the exports read one
 * result.
 */
export type RosterStatus = 'active' | 'detailed'

export const ROSTER_STATUS_LABEL: Record<RosterStatus, string> = { active: 'Active', detailed: 'Detailed' }

export type RosterRecord = {
  employeeId: string
  identifier: string
  employee: string
  seniorityDate: string
  teamId: string
  team: string
  title: string
  shift: string
  qualifications: string[]
  status: RosterStatus
}

export type RosterRow = RosterRecord & {
  // Position in the seniority order of everyone in scope, so it does not
  // change when the list is filtered.
  rank: number
  supervisorId: string | null
}

export type RosterFilters = {
  teamId?: string
  supervisorId?: string
  shiftName?: string
  status?: RosterStatus
  search?: string
}

// Earliest seniority date first; the identifier breaks a tie so the order is
// the same every time.
export function buildRoster(records: RosterRecord[], supervisors: Map<string, string>): RosterRow[] {
  return [...records]
    .sort((a, b) => a.seniorityDate.localeCompare(b.seniorityDate) || a.identifier.localeCompare(b.identifier))
    .map((record, index) => ({ ...record, rank: index + 1, supervisorId: supervisors.get(record.teamId) ?? null }))
}

export function filterRoster(rows: RosterRow[], filters: RosterFilters): RosterRow[] {
  return rows.filter(
    (row) =>
      (!filters.teamId || row.teamId === filters.teamId) &&
      (!filters.supervisorId || row.supervisorId === filters.supervisorId) &&
      (!filters.shiftName || row.shift === filters.shiftName) &&
      (!filters.status || row.status === filters.status) &&
      (!filters.search || matchesEmployee(filters.search, row.employee, row.identifier)),
  )
}
